// ============================================================================
// JARVISTRAVEL - CONCIERGE API
// Version: 1.0.0
// AI concierge conversations and messages
// ============================================================================

import { api } from "./client";
import { reportApiFailure } from './failure';
import type { TravelPace } from '@/app/types';
import { todayYmd } from '@/app/utils/dateOnly';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateConversationRequest {
  trip_id?: string;
}

export interface ConversationData {
  id: string;
  user_id: string;
  trip_id?: string;
  created_at: string;
  message_count: number;
}

export interface ListConversationsResponse {
  conversations: ConversationData[];
}

export interface SendMessageRequest {
  content: string;
  /** The traveller's own date, YYYY-MM-DD, which "tonight" and "tomorrow"
   *  resolve against (JAR-2465). */
  today?: string;
}

export interface MessageData {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
}

export interface SendMessageResponse {
  user_message: MessageData;
  assistant_reply: MessageData;
  conversation_id: string;
  /** A change to the plan the reply proposes (JAR-2465). Only on this
   *  response, never on a stored message; absent from a core without it. */
  proposed_change?: ProposedChange;
}

/** One side of a proposed change, as core checked it against the plan. */
export interface ChangeStop {
  name: string;
  day_id: string;
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM, 24-hour. */
  time: string;
}

/** A change to the plan a chat reply proposes. `stop` is the stop as it is now
 *  (replace, move and remove), and `place` as it will be (replace, move and
 *  add). `patch` is what /concierge/apply takes. */
export interface ProposedChange {
  action: 'replace' | 'move' | 'remove' | 'add' | 'add_stay' | 'add_flight';
  stop?: ChangeStop;
  place?: ChangeStop;
  /** Every action but add_stay and add_flight. A stay or a flight is not a stop,
   *  so it changes no day and carries nothing for the itinerary to apply
   *  (JAR-2630, JAR-3014). */
  patch?: ItineraryPatch;
  /** add_stay only: where the traveller said they are staying. Core sends it
   *  field for field as a NamedHotel, so it reaches the stay offer and Add a
   *  stay's own call without a second shape in between. */
  stay?: NamedHotel;
  /** add_flight only: the flight core matched from the number the traveller
   *  named (JAR-3014). It reaches the flight offer and the widget's own add +
   *  confirm calls without a second shape in between. */
  flight?: ProposedFlight;
}

/** A flight Jarvis proposes adding (JAR-3014).
 *
 *  `flight_number` and `date` are the re-add key: on accept the app passes them
 *  to the ordinary flight add, exactly as the flight widget does, and they
 *  resolve to this same service (`date` is the DEPARTURE date, so the add never
 *  has to ask which). The rest states the flight — its route and the two
 *  airport-local times. No fare and no seats: Jarvis states the flight, it does
 *  not sell it. */
export interface ProposedFlight {
  flight_number: string;
  date?: string;
  airline?: string;
  /** Departure airport IATA, e.g. "JFK". */
  origin?: string;
  /** Arrival airport IATA. */
  destination?: string;
  /** Airport-local ISO-8601 with offset, as every flight movement time is. */
  departs?: string;
  arrives?: string;
}

export interface ListMessagesResponse {
  messages: MessageData[];
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Create a new concierge conversation.
 * POST /v1/concierge/conversations
 */
export async function createConversation(tripId?: string, signal?: AbortSignal): Promise<ConversationData> {
  const body: CreateConversationRequest = {};
  if (tripId) body.trip_id = tripId;

  const result = await api.post<ConversationData>("/concierge/conversations", body, true, undefined, signal);

  if (result.error) {
    throw new Error(result.error.message || "Failed to create conversation");
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

/**
 * List conversations for the current user.
 * GET /v1/concierge/conversations
 */
export async function listConversations(tripId?: string, signal?: AbortSignal): Promise<ConversationData[]> {
  const path = tripId
    ? `/concierge/conversations?trip_id=${tripId}`
    : "/concierge/conversations";
  const result = await api.get<ListConversationsResponse>(path, true, signal);

  if (result.error) {
    throw new Error(result.error.message || "Failed to list conversations");
  }

  return result.data?.conversations || [];
}

/**
 * Send a message in a concierge conversation.
 * POST /v1/concierge/conversations/{conversationId}/messages
 */
export async function sendMessage(
  conversationId: string,
  content: string,
  signal?: AbortSignal,
): Promise<SendMessageResponse> {
  const result = await api.post<SendMessageResponse>(
    `/concierge/conversations/${conversationId}/messages`,
    { content, today: todayYmd() },
    true,
    undefined,
    signal,
  );

  if (result.error) {
    // The error itself, so its code and status survive: a trip that has ended
    // refuses with a sentence that is Jarvis's answer (useConcierge, JAR-1860).
    throw result.error;
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

/**
 * Get messages in a concierge conversation.
 * GET /v1/concierge/conversations/{conversationId}/messages
 */
export async function getMessages(
  conversationId: string,
  limit?: number,
  before?: string,
  signal?: AbortSignal,
): Promise<MessageData[]> {
  let path = `/concierge/conversations/${conversationId}/messages`;
  const params: string[] = [];
  if (limit) params.push(`limit=${limit}`);
  if (before) params.push(`before=${before}`);
  if (params.length > 0) path += `?${params.join("&")}`;

  const result = await api.get<ListMessagesResponse>(path, true, signal);

  if (result.error) {
    throw new Error(result.error.message || "Failed to get messages");
  }

  return result.data?.messages || [];
}

/**
 * Delete a conversation.
 * DELETE /v1/concierge/conversations/{conversationId}
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  const result = await api.del<{ success: boolean }>(
    `/concierge/conversations/${conversationId}`,
  );

  if (result.error) {
    throw new Error(result.error.message || "Failed to delete conversation");
  }
}

// ---------------------------------------------------------------------------
// Insert Idea (JT-CONC-002)
// ---------------------------------------------------------------------------

export interface ActivityCandidate {
  title: string;
  category: string;
  description?: string;
  estimated_cost: number;
  /** The currency `estimated_cost` is quoted in, when there's a price. */
  currency?: string;
  /** 0 when the provider gave no length core could read. */
  duration_min: number;
  location_name?: string;
  source_label?: string;
  /** The provider's own page for this idea, when it has one. Display only: it's
   *  never stored on the itinerary (JAR-2259). */
  provider_url?: string;
}

/** Core also sends day options, but they're constants; the card rates the
 *  chosen day from the trip's own days instead (JAR-2119). */
export interface InsertIdeaResponse {
  candidates: ActivityCandidate[];
}

/**
 * Search for activities to insert into the trip.
 * POST /v1/concierge/insert-idea
 */
export async function insertIdea(tripId: string, query: string): Promise<InsertIdeaResponse | null> {
  const result = await api.post<InsertIdeaResponse>("/concierge/insert-idea", {
    trip_id: tripId,
    query,
  });
  if (result.error) {
    reportApiFailure("[concierge] insert-idea failed:", result.error);
    // Thrown, not null: the caller needs the client's error to tell a trip
    // that has ended from a failure worth retrying (JAR-1864).
    throw result.error;
  }
  return result.data || null;
}

// ---------------------------------------------------------------------------
// Trip brief (JAR-2278)
// ---------------------------------------------------------------------------

/** Where the traveller says they're staying, as they named it: a hotel, or a
 *  rental from its host (JAR-2584). Its own dates are null unless they gave
 *  the stay dates of its own. */
export interface NamedHotel {
  name: string;
  /** Absent from a core that predates rentals, which reads it as a hotel. */
  kind?: 'hotel' | 'rental';
  // The trip brief sends these as null when it has none; a chat reply's stay
  // omits them instead (JAR-2630). stayForNamedHotel treats absent and null
  // the same, so both shapes are the same stay.
  address?: string | null;
  /** Its web address when they gave one: a hotel's site, or a listing's link. */
  link?: string | null;
  check_in?: string | null;
  check_out?: string | null;
}

/** What Jarvis read from a trip request typed in the planner. A field the text
 *  didn't give is null (the lists are empty), never a guess. */
export interface TripBrief {
  destination: string | null;
  places: string[];
  start_date: string | null;
  end_date: string | null;
  travelers: { adults: number; children: number } | null;
  pace: TravelPace | null;
  budget: { amount: number; currency: string } | null;
  day_window: DayWindow | null;
  /** The hotel they're staying at, which becomes the trip's stay (JAR-2439).
   *  Absent from a core that predates it. */
  hotel?: NamedHotel | null;
  wants: string[];
  /** One line a day, by day number from 1, when both dates are known. */
  outline: { day: number; place: string; focus: string }[];
  /** What Jarvis couldn't tell, such as a missing year. */
  questions: string[];
}

/**
 * Jarvis reads a typed trip request. Nothing is saved, and it waits for the
 * model. `today` is the person's local date, which dates without a year are
 * read against.
 * POST /v1/concierge/trip-brief
 */
export async function tripBrief(text: string, today: string): Promise<TripBrief | null> {
  const result = await api.post<TripBrief>("/concierge/trip-brief", { text, today });
  if (result.error) {
    reportApiFailure("[concierge] trip-brief failed:", result.error);
    throw result.error;
  }
  return result.data || null;
}

// ---------------------------------------------------------------------------
// Rebuild Day (JT-CONC-003)
// ---------------------------------------------------------------------------

export interface PatchOp {
  op: string;
  day_id?: string;
  item_id?: string;
  item_json?: string;
  /** move: the stop's new time, HH:MM (JAR-2465). */
  time?: string;
}

export interface ItineraryPatch {
  source_version_id?: string;
  /** Core's token for the trip as it made this patch: apply refuses the patch
   *  with a 409 PLAN_CHANGED once the trip has changed since (JAR-2783). It is
   *  echoed as core sent it, never computed here. */
  based_on_version?: string;
  operations: PatchOp[];
  fi_delta: number;
  budget_delta: number;
}

export interface RebuildDayResponse {
  patch: ItineraryPatch;
  rationale: string;
}

/** The hours a planned day fits inside, as 24-hour "HH:MM". */
export interface DayWindow {
  start: string;
  end: string;
}

/** What Jarvis plans a day around (JAR-2280). Every field is optional: core
 *  plans the day from the trip alone without them. */
export interface DayPlanAsk {
  date?: string;
  /** Where the day is spent, when it isn't the trip's destination. */
  place?: string;
  /** The day's theme from the brief's outline. */
  focus?: string;
  day_window?: DayWindow;
  wants?: string[];
  /** Plan the trip's hotel as the day's first stop. */
  include_hotel?: boolean;
  /** The re-plan that follows a flight the traveller placed. The day an
   *  overnight flight lands then starts light: core plans nothing new on it
   *  and removes only what the flight rules out (JAR-2597). */
  after_flight?: boolean;
}

/**
 * Rebuild a day around specified anchor items. Jarvis plans the day with named
 * places, so the call waits for the model (JAR-2279).
 * POST /v1/concierge/rebuild-day
 */
export async function rebuildDay(
  tripId: string,
  dayId: string,
  anchorIds?: string[],
  ask?: DayPlanAsk,
): Promise<RebuildDayResponse | null> {
  const result = await api.post<RebuildDayResponse>("/concierge/rebuild-day", {
    trip_id: tripId,
    day_id: dayId,
    anchor_ids: anchorIds || [],
    ...ask,
  });
  if (result.error) {
    reportApiFailure("[concierge] rebuild-day failed:", result.error);
    // Thrown, not null: the caller needs the client's error to tell a trip
    // that has ended from a failure worth retrying (JAR-1864).
    throw result.error;
  }
  return result.data || null;
}

// ---------------------------------------------------------------------------
// Apply Patch
// ---------------------------------------------------------------------------

export interface ApplyResponse {
  success: boolean;
  version_id?: string;
  message: string;
}

/**
 * Apply a concierge patch and create a new trip version.
 * POST /v1/concierge/apply
 */
export async function applyPatch(
  tripId: string,
  patch: ItineraryPatch
): Promise<ApplyResponse | null> {
  const result = await api.post<ApplyResponse>("/concierge/apply", {
    trip_id: tripId,
    patch,
  });
  if (result.error) {
    reportApiFailure("[concierge] apply failed:", result.error);
    // Thrown, not null: the caller needs the client's error to tell a trip
    // that has ended from a failure worth retrying (JAR-1864).
    throw result.error;
  }
  return result.data || null;
}
